import type { MatchSource } from '@apm/contracts';
import { matchSourceKey } from '../handoff.js';
import { useT } from '../i18n/LocaleProvider.js';
import { QrOverlay } from './QrOverlay.js';

/**
 * Tier 1·2 결과 안내 — "휴대폰 N대로 알림을 보냈습니다" (`docs/07` §4 시나리오 B-4).
 *
 * **재생을 멈추지 않는다** (절대 규칙 7·14). 이 오버레이가 뜨는 동안에도 영상은 계속
 * 가고, 시청자는 폰을 집어 드는 동안 채널을 떠나지 않는다 — 그것이 이 시스템의 핵심
 * 가치이고, Tier 2 가 Tier 0 과 다른 점 전부다.
 *
 * **대수를 그대로 보여 준다.** 서버의 `matchedCount` 는 **실제 발송 성공 수**라
 * (`docs/13` §1.5) 화면의 숫자와 실제로 울리는 폰의 수가 어긋나지 않는다.
 *
 * ── 판정 근거와 QR (`docs/21` W7·W8) ────────────────────────────
 *
 * `source` 는 **무엇으로 찾았는지**를 시청자에게 말한다. 없으면 그 줄을 그리지 않는다 —
 * 위탁형 응답에는 그 값이 없고, 없다고 화면이 깨지면 안 된다.
 *
 * `qrUrl` 은 **두 번째 선택지**다. 가구 경로에서만 온다 — 폰이 다른 망에 있어 알림이
 * 닿지 않을 수 있고, 그때 시청자가 지금 할 수 있는 일을 남겨 두는 것이다. 없으면 그
 * 자리를 통째로 비운다(계정 경로에는 QR 이 없다).
 *
 * **QR 은 작게, 안내 옆에 둔다.** 크게 그리면 알림이 실패한 화면처럼 읽힌다 — 어디까지나
 * 두 번째 선택지라는 인상이어야 한다.
 */
export function PushSentOverlay({
  count,
  source,
  qrUrl,
}: {
  count: number;
  source?: MatchSource;
  qrUrl?: string;
}) {
  const t = useT();
  const sourceKey = matchSourceKey(source);

  return (
    <div className="overlay overlay--handoff">
      <div className="overlay__handoff-text">
        {sourceKey !== null && <p className="overlay__handoff-source">{t(sourceKey)}</p>}
        <p className="overlay__handoff-title">{t('handoff.pushSent', { n: count })}</p>
        <p className="overlay__handoff-hint">
          {qrUrl === undefined ? t('handoff.pushSentHint') : t('handoff.pushSentWithQrHint')}
        </p>
      </div>
      {qrUrl !== undefined && (
        <div className="overlay__handoff-qr">
          <QrOverlay url={qrUrl} hint={t('handoff.qrIfSilent')} size={96} bare />
        </div>
      )}
    </div>
  );
}
